import React, { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Quote, Star } from "lucide-react";
import { api } from "../lib/api";

/**
 * Editorial-style testimonials carousel.
 * - Auto-hides when there are no testimonials.
 * - Snap-scroll on mobile, arrow controls on desktop.
 */
export default function Testimonials() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const scrollerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/testimonials");
        if (!cancelled) setItems(r.data || []);
      } catch (_) {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading || items.length === 0) return null;

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: Math.round(el.clientWidth * 0.9) * dir, behavior: "smooth" });
  };

  return (
    <section className="mt-32" data-testid="testimonials-section">
      <div className="flex items-end justify-between mb-12 gap-6">
        <div>
          <p className="eyebrow">In their words</p>
          <h2 className="font-display text-3xl md:text-5xl mt-3 tracking-tighter">
            What our clients <em className="font-medium italic">are saying.</em>
          </h2>
        </div>
        {items.length > 1 && (
          <div className="hidden md:flex items-center gap-2" data-testid="testimonials-controls">
            <button
              type="button"
              onClick={() => scrollBy(-1)}
              className="w-12 h-12 border border-[var(--mn-ink)] flex items-center justify-center hover:bg-[var(--mn-ink)] hover:text-white transition-colors"
              aria-label="Previous testimonial"
              data-testid="testimonials-prev"
            >
              <ChevronLeft size={18} strokeWidth={1.5} />
            </button>
            <button
              type="button"
              onClick={() => scrollBy(1)}
              className="w-12 h-12 border border-[var(--mn-ink)] flex items-center justify-center hover:bg-[var(--mn-ink)] hover:text-white transition-colors"
              aria-label="Next testimonial"
              data-testid="testimonials-next"
            >
              <ChevronRight size={18} strokeWidth={1.5} />
            </button>
          </div>
        )}
      </div>

      <div
        ref={scrollerRef}
        className="flex gap-6 md:gap-10 overflow-x-auto pb-4 snap-x snap-mandatory scroll-smooth no-scrollbar"
        data-testid="testimonials-scroller"
      >
        {items.map((t) => (
          <article
            key={t.id}
            className="snap-start shrink-0 w-[88%] sm:w-[60%] md:w-[42%] lg:w-[32%] border border-[var(--mn-border-strong)] p-8 md:p-10 bg-white flex flex-col"
            data-testid={`testimonial-${t.id}`}
          >
            <Quote size={28} strokeWidth={1.2} className="text-[var(--mn-accent)] shrink-0" />
            <p className="font-display text-2xl md:text-3xl leading-[1.25] tracking-tight mt-6 flex-1">
              {`\u201C${t.quote}\u201D`}
            </p>
            <div className="mt-8 pt-6 border-t border-[var(--mn-border)] flex items-center justify-between gap-4">
              <div className="min-w-0">
                <p className="font-medium text-sm" data-testid="testimonial-name">{t.name}</p>
                {t.location && (
                  <p className="text-[10px] uppercase tracking-[0.22em] text-[var(--mn-ink-2)] mt-1">
                    {t.location}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-0.5 shrink-0" aria-label={`Rating ${t.rating} out of 5`}>
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star
                    key={i}
                    size={13}
                    strokeWidth={1.4}
                    className={i < (t.rating || 5) ? "text-[var(--mn-ink)] fill-[var(--mn-ink)]" : "text-[var(--mn-border-strong)]"}
                  />
                ))}
              </div>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}
